import React, { useEffect, useMemo, useState } from "react";
import { InputAdornment, MenuItem, Radio } from "@mui/material";
import {
	CustomDialog,
	CustomTextField,
	icon,
	IconComponent,
	InfiniteListSelector,
} from "../../../../../../common/components";
import styles from "./LaunchDialog.module.css";
import { translate } from "../../../../../../common/providers";
import { isNonEmptyArray, isNonEmptyObject } from "../../../../../../common/utils";
import { highlightText } from "../../utils";
import { BackOfficeService } from "../../../../../../api";
import { useApi } from "../../../../../../common/hooks";

const USE_PROJECT_SELECTOR = false;

export default function LaunchDialog({ agent, executedAgent, open, onClose, onLaunch }) {
	const { title, prompt } = agent || {};
	const { agentName, prompt: executedPrompt } = executedAgent || {};
	const [tempValues, setTempValues] = useState([]);
	const [tempExecutedValues, setTempExecutedValues] = useState({});
	const [openConfirmation, setOpenConfirmation] = useState(false);
	const [project, setProject] = useState(null);
	const [company, setCompany] = useState(null);
	const [projectsRequest, setProjectsRequest] = useState(null);
	const isValidForm = useMemo(() => {
		const isValidVariables = tempValues.every((val) => val?.length > 0);
		const isValidExecutedParameters = Object.values(tempExecutedValues).every((val) => val?.length > 0);
		return (isValidVariables || isValidExecutedParameters) && project;
	}, [tempValues, tempExecutedValues, project]);
	const { call: getAllProjects } = useApi(BackOfficeService.getAllProjects);
	const uniqueVariables = useMemo(() => [...new Set(agent?.variables || [])], [agent]);
	useEffect(() => {
		if (USE_PROJECT_SELECTOR) {
			setProjectsRequest(
				() =>
					({ limit, page }) =>
						getAllProjects({
							limit,
							page,
						})
			);
		}
	}, [getAllProjects]);
	useEffect(() => {
		if (isNonEmptyArray(agent?.variables)) {
			setTempValues(Array(agent.variables.length).fill(""));
		}
	}, [agent]);
	useEffect(() => {
		if (isNonEmptyObject(executedAgent?.parameters)) {
			setTempExecutedValues({ ...executedAgent.parameters });
			setProject(executedAgent.projectId);
		}
	}, [executedAgent]);
	const resetState = () => {
		setTempValues([]);
		setTempExecutedValues({});
		setProject(null);
		setOpenConfirmation(false);
	};
	const handleDefinitiveClose = () => {
		resetState();
		onClose();
	};
	const handleClose = () => {
		const anyChange = tempValues.length > 0;
		if (anyChange) {
			setOpenConfirmation(true);
		} else {
			handleDefinitiveClose();
		}
	};
	const handleUpdateVariable = (value, id) => {
		setTempValues((prev) => prev.map((val, index) => (index === id ? value : val)));
	};
	const handleUpdateExecutedVariable = (value, key) => {
		setTempExecutedValues((prev) => ({ ...prev, [key]: value }));
	};
	const handleChangeProject = (newProject) => {
		setProject(newProject);
	};
	const handleSubmit = () => {
		if (isValidForm && onLaunch && typeof onLaunch === "function") {
			if (agent) {
				const parameters = Object.fromEntries(
					uniqueVariables.map((key, index) => [key, tempValues[index]]) || []
				);
				onLaunch({
					agentId: agent.id,
					projectId: Number.parseInt(project, 10),
					companyId: Number.parseInt(company, 10),
					parameters,
				});
			} else if (executedAgent) {
				onLaunch({
					agentId: executedAgent.agentId,
					projectId: Number.parseInt(project, 10),
					companyId: Number.parseInt(company, 10),
					parameters: tempExecutedValues,
				});
			}
		}
	};
	const endAdornmentRenderer = (cond) => (
		<InputAdornment position="end">
			<IconComponent color="var(--grey-600)" icon={cond ? icon.faCaretUp : icon.faCaretDown} />
		</InputAdornment>
	);
	const selectorRowRenderer = ({ loadedRow, onClick }) => (
		<MenuItem key={loadedRow?.id} onClick={() => onClick(loadedRow)}>
			<Radio checked={loadedRow?.id === project?.id} />
			<span>{loadedRow?.projectName || loadedRow?.name || ""}</span>
		</MenuItem>
	);
	return (
		<CustomDialog
			disableBackdropClick
			disableEscapeKeyDown
			displayCloseCross
			isForm
			className={styles.dialog}
			open={open}
			submitDisabled={!isValidForm}
			submitLabel={translate("common:btn.validate")}
			title={translate("dsi.agents.dialog.title.launch")}
			onClose={handleClose}
			onSubmit={handleSubmit}
		>
			<div className={styles.input}>{title || agentName}</div>
			<div
				className={`${styles.promptInput} ${styles.input}`}
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{
					__html:
						(prompt?.length > 0 && highlightText(prompt)) ||
						(executedPrompt?.length > 0 && highlightText(executedPrompt)) ||
						null,
				}}
			/>
			{(isNonEmptyArray(uniqueVariables) &&
				uniqueVariables.map((value, index) => (
					<div key={value} className={styles.valueInput}>
						<div>{value}</div>
						<CustomTextField
							fullWidth
							multiline
							maxRows={3}
							size="small"
							value={tempValues[index]}
							variant="outlined"
							onChange={(e) => handleUpdateVariable(e.target.value, index)}
						/>
					</div>
				))) ||
				(isNonEmptyObject(executedAgent?.parameters) &&
					Object.entries(executedAgent?.parameters).map(([key, value]) => (
						<div key={key} className={styles.valueInput}>
							<div>{key}</div>
							<CustomTextField
								fullWidth
								multiline
								maxRows={3}
								size="small"
								value={tempExecutedValues[key] ?? value}
								variant="outlined"
								onChange={(e) => handleUpdateExecutedVariable(e.target.value, key)}
							/>
						</div>
					)))}
			<div>{translate("dsi.user-details.th.company")}</div>
			<CustomTextField
				fullWidth
				InputProps={{ inputProps: { min: 0 } }}
				size="small"
				type="number"
				value={company || executedAgent?.companyId}
				variant="outlined"
				onChange={(e) => setCompany(e.target.value)}
			/>
			<div>{translate("dsi.user-details.th.project")}</div>
			<CustomTextField
				fullWidth
				InputProps={{ inputProps: { min: 0 } }}
				size="small"
				type="number"
				value={project || executedAgent?.projectId}
				variant="outlined"
				onChange={(e) => setProject(e.target.value)}
			/>
			{USE_PROJECT_SELECTOR && (
				<InfiniteListSelector
					fullWidth
					endAdornmentRenderer={endAdornmentRenderer}
					infiniteListRequest={projectsRequest}
					rowRenderer={selectorRowRenderer}
					value={project?.projectName || project?.name || translate("dsi.user-details.th.project")}
					onClick={handleChangeProject}
				/>
			)}
			<CustomDialog
				iconColor="var(--red-500)"
				iconModel={icon.faExclamationTriangle}
				open={openConfirmation}
				submitLabel={translate("common:btn.confirm")}
				subTitle={translate(`dsi.agents.warning.${(agent && "modification") || "creation"}.cancel`)}
				onClose={() => setOpenConfirmation(false)}
				onSubmit={handleDefinitiveClose}
			/>
		</CustomDialog>
	);
}
